import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { toast } from "sonner";
import { ResourceCard } from "@/pages/LearningHub";
import {
    ArrowLeft,
    Building2,
    Search,
    Loader2,
    BookOpen,
    Filter,
    X,
} from "lucide-react";

// ---------------------------------------------------------------
// LearningDepartmentAll — the "View all" sub-page for a single
// department. Fetches every resource whose owning collection lives in
// this dept and renders them as a flat grid grouped by collection so the
// context is preserved. Reuses `<ResourceCard>` exported from LearningHub
// so users get full parity — status flip, notes, open, react, move,
// copy, edit, delete — exactly as they would inside a collection.
// ---------------------------------------------------------------
const RESOURCE_TYPES = [
    { id: "video", label: "Video" },
    { id: "course", label: "Course" },
    { id: "article", label: "Article" },
    { id: "doc", label: "Doc" },
    { id: "podcast", label: "Podcast" },
    { id: "book", label: "Book" },
    { id: "other", label: "Other" },
];

const LearningDepartmentAll = () => {
    const { deptId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { users, depts } = useData();

    const dept = useMemo(() => depts.find((d) => d.id === deptId), [depts, deptId]);

    const [resources, setResources] = useState([]);
    const [deptCollections, setDeptCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [collectionFilter, setCollectionFilter] = useState("all");

    const load = async () => {
        try {
            setLoading(true);
            // Fetch resources AND collections in parallel. Fetching
            // collections separately fixes an old bug where newly-created,
            // still-empty collections never showed up on this page because
            // it derived the collection list purely from resources.
            const [rRes, cRes] = await Promise.all([
                api.get("/learning/resources", { params: { department_id: deptId } }),
                api.get("/learning/collections", { params: { department_id: deptId } }).catch(() => ({ data: [] })),
            ]);
            setResources(rRes.data || []);
            setDeptCollections(cRes.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load resources");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [deptId]);

    // Union of collections that either (a) belong to this dept OR (b) show up
    // as an owner of one of the fetched resources. This way an empty
    // collection created today still appears in the picker AND on the page
    // as an "empty" bucket.
    const collectionsInDept = useMemo(() => {
        const map = new Map();
        for (const c of deptCollections) {
            map.set(c.id, { id: c.id, name: c.name || "Untitled collection", empty: (c.resource_count || 0) === 0 });
        }
        for (const r of resources) {
            if (r.collection_id && !map.has(r.collection_id)) {
                map.set(r.collection_id, { id: r.collection_id, name: r.collection_name || "Untitled collection", empty: false });
            }
        }
        return Array.from(map.values()).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [deptCollections, resources]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        return resources.filter((r) => {
            if (typeFilter !== "all" && r.type !== typeFilter) return false;
            if (collectionFilter !== "all" && r.collection_id !== collectionFilter) return false;
            if (!s) return true;
            return (
                (r.name || "").toLowerCase().includes(s) ||
                (r.description || "").toLowerCase().includes(s) ||
                (r.url || "").toLowerCase().includes(s) ||
                (r.collection_name || "").toLowerCase().includes(s) ||
                (r.tags || []).some((t) => (t || "").toLowerCase().includes(s))
            );
        });
    }, [resources, q, typeFilter, collectionFilter]);

    // Group filtered resources by collection so the page keeps
    // collection context visible even when everything is on one page.
    // Empty collections (no resources at all) get their own bucket with
    // items=[] so the page still tells the user "yes it exists".
    const grouped = useMemo(() => {
        const buckets = new Map();
        // Seed with EVERY collection in this dept so brand-new empty ones
        // show up as an "(empty)" section, not disappear.
        for (const c of collectionsInDept) {
            buckets.set(c.id, { id: c.id, name: c.name, items: [] });
        }
        for (const r of filtered) {
            const key = r.collection_id || "_none";
            if (!buckets.has(key)) buckets.set(key, { id: key, name: r.collection_name || "Untitled collection", items: [] });
            buckets.get(key).items.push(r);
        }
        // Respect the current collection filter — if the user picked one,
        // hide every other bucket.
        const arr = Array.from(buckets.values());
        const scoped = collectionFilter === "all" ? arr : arr.filter((b) => b.id === collectionFilter);
        // While an active text/type filter is on we hide empty buckets so
        // the page doesn't show unrelated "0 results" rows. With no
        // filters, empty buckets are useful (they prove the collection
        // exists) so we keep them.
        const hasActiveTextOrType = (q || "").trim().length > 0 || typeFilter !== "all";
        const visible = hasActiveTextOrType ? scoped.filter((b) => b.items.length > 0) : scoped;
        return visible.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [collectionsInDept, filtered, collectionFilter, q, typeFilter]);

    // ------- Resource action handlers (parity with CollectionDetailDialog) -------
    // Optimistic local drop; API confirms.
    const removeRes = async (rid) => {
        if (!window.confirm("Remove this resource?")) return;
        setResources((rs) => rs.filter((r) => r.id !== rid));
        try {
            await api.delete(`/learning/resources/${rid}`);
            toast.success("Resource removed");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
            load();
        }
    };
    const setStatus = async (rid, status) => {
        setResources((rs) => rs.map((r) => (r.id === rid ? { ...r, my_status: status } : r)));
        try {
            await api.post(`/learning/resources/${rid}/status`, { status });
        } catch {
            load();
        }
    };

    const activeFilters = (typeFilter !== "all" ? 1 : 0) + (collectionFilter !== "all" ? 1 : 0) + (q ? 1 : 0);

    return (
        <div className="space-y-6" data-testid="learning-dept-all-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <button
                        onClick={() => navigate("/learning")}
                        data-testid="learning-dept-all-back"
                        className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#25908A] font-medium transition-colors mb-2"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to Learning Hub
                    </button>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#25908A] font-semibold flex items-center gap-1.5">
                        <Building2 className="h-3 w-3" /> Department library
                    </div>
                    <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-3">
                        <BookOpen className="h-7 w-7 text-[#2EB4AC]" />
                        {dept?.name || "Department"}
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {loading ? "Loading resources…" : `${resources.length} resource${resources.length === 1 ? "" : "s"} across ${collectionsInDept.length} collection${collectionsInDept.length === 1 ? "" : "s"}`}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="relative flex-1 max-w-lg">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by title, tag, or link…"
                        data-testid="learning-dept-all-search"
                        className="w-full h-10 pl-9 pr-3 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none"
                    />
                </div>
                <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    data-testid="learning-dept-all-type-filter"
                    className="h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none"
                >
                    <option value="all">All types</option>
                    {RESOURCE_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                <select
                    value={collectionFilter}
                    onChange={(e) => setCollectionFilter(e.target.value)}
                    data-testid="learning-dept-all-collection-filter"
                    className="h-10 px-3 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#2EB4AC]/25 focus:border-[#2EB4AC] outline-none min-w-[180px]"
                >
                    <option value="all">All collections</option>
                    {collectionsInDept.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {activeFilters > 0 && (
                    <button
                        onClick={() => { setQ(""); setTypeFilter("all"); setCollectionFilter("all"); }}
                        data-testid="learning-dept-all-clear-filters"
                        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                    >
                        <X className="h-3.5 w-3.5" /> Clear
                    </button>
                )}
                {activeFilters > 0 && (
                    <div className="text-xs text-slate-500 inline-flex items-center gap-1.5 ml-auto">
                        <Filter className="h-3.5 w-3.5" />
                        <span>{filtered.length} of {resources.length}</span>
                    </div>
                )}
            </div>

            {/* Content */}
            {loading ? (
                <div className="text-center py-16"><Loader2 className="h-6 w-6 mx-auto animate-spin text-[#2EB4AC]" /></div>
            ) : grouped.length === 0 ? (
                <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-10 text-center">
                    <BookOpen className="h-8 w-8 text-[#2EB4AC] mx-auto" />
                    <p className="mt-3 font-heading font-semibold text-slate-900">
                        {resources.length === 0 && collectionsInDept.length === 0 ? "No collections yet" : "Nothing matches your filters"}
                    </p>
                    <p className="text-slate-500 text-sm mt-1">
                        {resources.length === 0 && collectionsInDept.length === 0
                            ? "Head back to the Learning Hub to create a collection."
                            : "Try clearing filters or searching a different term."}
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    {grouped.map((g) => (
                        <section key={g.id} data-testid={`learning-dept-all-group-${g.id}`}>
                            <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-100">
                                <h2 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                                    {g.name}
                                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{g.items.length}</span>
                                </h2>
                            </div>
                            {g.items.length === 0 ? (
                                <div className="text-xs text-slate-400 italic px-1 py-3 border border-dashed border-slate-200 rounded-md bg-slate-50/60">
                                    This collection is empty. Add resources from the Learning Hub.
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {g.items.map((r) => (
                                    <ResourceCard
                                        key={r.id}
                                        r={r}
                                        canDelete={r.collection_owner_id === user?.user_id || user?.role === "super_admin"}
                                        canEdit={r.collection_owner_id === user?.user_id || user?.role === "super_admin"}
                                        onEdit={() => toast.info("Open the collection to edit metadata")}
                                        onRemove={() => removeRes(r.id)}
                                        onStatus={(s) => setStatus(r.id, s)}
                                        isOwner={r.collection_owner_id === user?.user_id || user?.role === "super_admin"}
                                        onAssign={() => toast.info("Open the collection to change assignments")}
                                        allUsers={users}
                                        collectionAssignees={[]}
                                        onMoved={async ({ mode }) => {
                                            // Move: drop locally. Copy: reload to see the new dupe if
                                            // its target collection is still in this dept.
                                            if (mode !== "copy") setResources((rs) => rs.filter((x) => x.id !== r.id));
                                            else load();
                                        }}
                                    />
                                ))}
                                </div>
                            )}
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LearningDepartmentAll;
